import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["external", "href"],
  _excluded2 = ["children", "useWrappingLink"],
  _excluded3 = ["children", "className", "externalLink", "externalLinkText", "fwdRef", "href", "imgProps", "imgSrc", "interactiveArea", "onClick", "rel", "tagName", "title", "titleTagName", "wrapContent"];
import React, { forwardRef, useCallback } from 'react';
import { IconLinkExternal, IconLinkInternal } from '@op/opds.opux-icons/navigation';
import { buildVersionedClass, buildVersionedClassNames } from '@op/opds.opux-utilities';
import TileContent from './tile-content';
import { useLocale } from '../common/locale';
import messages from '../action/_messages';
const Link = _ref => {
  let {
      external,
      href
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const rel = external ? 'noreferrer' : undefined;
  const role = href ? undefined : 'button';
  const target = external ? '_blank' : undefined;
  return /*#__PURE__*/React.createElement("a", _extends({
    href: href,
    rel: rel,
    role: role,
    tabIndex: 0,
    target: target
  }, props));
};

// Handle case when there is `href` or `onClick` but no title.
const LinkWrap = _ref2 => {
  let {
      children,
      useWrappingLink
    } = _ref2,
    props = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  return useWrappingLink ? /*#__PURE__*/React.createElement(Link, _extends({
    className: buildVersionedClass('ds-tile__link-wrap')
  }, props), children) : /*#__PURE__*/React.createElement(React.Fragment, null, children);
};

/**
 * _Tile contains a set of content that should act as an entry point to more detailed information._
 *
 * ***
 *
 * Tile is a container for content. It is typically meant to be used for
 * multi-column layouts, that provide entry points to detailed information on
 * different topics.
 *
 * The component props allow controlling the interactivity of
 * its title or the whole content, including an image and marking the HTML with
 * relevant semantic tags.
 *
 * Additional props (including `ref`) will be forwarded to the wrapping HTML element.
 */
export const Tile = _ref3 => {
  let {
      children,
      className,
      externalLink = false,
      externalLinkText,
      fwdRef,
      href,
      imgProps = {
        alt: ''
      },
      imgSrc,
      interactiveArea = 'title',
      onClick,
      rel: relProp,
      tagName: TileTag = 'div',
      title,
      titleTagName: TitleTag = 'h2',
      wrapContent = true
    } = _ref3,
    props = _objectWithoutPropertiesLoose(_ref3, _excluded3);
  const lc = useLocale();
  const stretchedLink = interactiveArea === 'tile';
  const useWrappingLink = !title && (href || onClick);
  const classes = buildVersionedClassNames('ds-tile', className, {
    'ds-tile--link-stretched': stretchedLink,
    'ds-tile--link-wrap': useWrappingLink,
    'ds-tile--linked': href || onClick,
    'ds-tile--with-image': imgSrc
  });
  const Icon = externalLink ? IconLinkExternal : IconLinkInternal;
  const iconLabel = externalLink ? externalLinkText || messages.externalLink[lc] : undefined;
  const Content = wrapContent ? TileContent : React.Fragment;
  const rel = href ? relProp ? relProp : 'noopener' : undefined;
  const onKeyDown = useCallback(event => {
    if (onClick && !href) {
      if (event.key === 'Enter') {
        onClick();
      } else if (event.key === ' ') {
        event.preventDefault();
        onClick();
      }
    }
  }, [href, onClick]);
  const Title = useCallback(() => {
    return href || onClick ? /*#__PURE__*/React.createElement(Link, {
      className: buildVersionedClass('ds-tile__link'),
      external: externalLink,
      href: href,
      onClick: onClick,
      onKeyDown: onKeyDown,
      rel: rel
    }, title, /*#__PURE__*/React.createElement(Icon, {
      className: buildVersionedClass('ds-tile__icon'),
      color: "action",
      label: iconLabel
    })) : /*#__PURE__*/React.createElement(React.Fragment, null, title);
  }, [Icon, externalLink, href, iconLabel, onClick, onKeyDown, rel, title]);
  return (
    /*#__PURE__*/
    // @ts-expect-error -- Typing fwdRef as HTMLElement will not work with any tag therefore we just ignore the error
    React.createElement(TileTag, _extends({}, props, {
      className: classes,
      ref: fwdRef
    }), /*#__PURE__*/React.createElement(LinkWrap, {
      external: externalLink,
      href: href,
      onClick: onClick,
      onKeyDown: onKeyDown,
      rel: rel,
      useWrappingLink: !!useWrappingLink
    }, title && /*#__PURE__*/React.createElement(TitleTag, {
      className: buildVersionedClass('ds-tile__title')
    }, /*#__PURE__*/React.createElement(Title, null)), children && /*#__PURE__*/React.createElement(Content, null, children), imgSrc && /*#__PURE__*/React.createElement("div", {
      className: buildVersionedClass('ds-tile__image')
    }, /*#__PURE__*/React.createElement("img", _extends({
      src: imgSrc
    }, imgProps)))))
  );
};
const Fwd = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/React.createElement(Tile, _extends({}, props, {
  fwdRef: ref
})));
Fwd.displayName = 'Tile';
export default Fwd;